import {
    BlotterColumn,
    BlotterColumns,
    useColumnDefinitions,
    useColumnPortfolio,
} from 'widgets/blotter/columns/ColumnPortfolio';
import {
    ColumnDefinitionsDictionary,
    ValueType,
    toUserCurrencyColumnName,
} from '@instinet-analytics/semantic-ui-components';
import { numberFormatUtils } from '@instinet-analytics/core-lib';
import { useContext, useMemo } from 'react';
import { blotterColumnDefinitions } from 'widgets/blotter/blotterGridOptions';
import AppContext, { IndigoAppContextType } from 'app/AppContext';
import { useOutliersConfigSlice } from 'widgets/outliers/store/config/configSlice';
import { getColDefsForColumnDefinitionsDictionary } from '@instinet-analytics/semantic-ui-components';
import { useSelectIsFeatureEnabled } from 'app/store/featureToggles/selectors';
import { WellKnownFeatureToggleKeys } from 'app/store/featureToggles/FeatureTogglesState';
import { currencyCellRenderer } from 'widgets/blotter/cellRenderers/currencyCellRenderer';

const { N } = numberFormatUtils;

export type OutliersAdditionalColumnWithoutLastPrice =
    | 'pnl'
    | 'benchmarkPerformance'
    | 'arrivalPAdj'
    | 'arrivalPDAdj'
    | 'value';
export type OutliersAdditionalColumnWithLastPrice =
    | 'realisedPnl'
    | 'unrealisedPnl'
    | 'totalPnl';
export type OutliersAdditionalColumn =
    | OutliersAdditionalColumnWithoutLastPrice
    | OutliersAdditionalColumnWithLastPrice;
export type OutliersColumn = BlotterColumn | OutliersAdditionalColumn;

export const getAdditionalOutlierColumns: (
    isLastPriceEnabled: boolean
) => ColumnDefinitionsDictionary<OutliersAdditionalColumn> = (
    isLastPriceEnabled
) => {
    const ret: ColumnDefinitionsDictionary<OutliersAdditionalColumnWithoutLastPrice> =
        {
            pnl: {
                field: 'pnl',
                name: toUserCurrencyColumnName('P&L'),
                width: 70,
                valueType: ValueType.CURRENCY,
                precision: 0,
                tag: 'P&L Data/P & L',
                desc: isLastPriceEnabled
                    ? 'Realized, unrealized or total P&L (depending on settings)'
                    : 'Realized P&L',
            },

            arrivalPAdj: {
                field: 'arrivalPAdj',
                name: 'Adjusted Arrival Price',
                width: 70,
                valueType: ValueType.NUMBER,
                precision: 2,
                tag: 'Order Data/Adjusted Arrival Price',
                desc: 'Adjusted Arrival Price',
            },
            arrivalPDAdj: {
                field: 'arrivalPDAdj',
                name: toUserCurrencyColumnName('Adjusted Arrival Price'),
                width: 70,
                valueType: ValueType.CURRENCY,
                precision: 2,
                tag: 'Order Data/Adjusted Arrival Price',
                desc: 'Adjusted Arrival Price',
            },
            benchmarkPerformance: {
                field: 'benchmarkPerformance',
                name: 'P&L bps',
                width: 70,
                valueType: ValueType.NUMBER,
                precision: 0,
                tag: 'P&L Data/P&L bps',
                desc: 'P&L bps',
            },
            value: {
                field: 'value',
                name: toUserCurrencyColumnName('Order Value'),
                width: 70,
                valueType: ValueType.CURRENCY,
                precision: 0,
                tag: 'P&L Data/Order Value',
                desc: 'Order Value (Order Qty * PNC)',
            },
        };

    if (!isLastPriceEnabled) {
        return ret as ColumnDefinitionsDictionary<OutliersAdditionalColumn>;
    }

    const lastPriceColumns: ColumnDefinitionsDictionary<OutliersAdditionalColumnWithLastPrice> =
        {
            realisedPnl: {
                field: 'realisedPnl',
                name: toUserCurrencyColumnName('Realized P&L'),
                width: 70,
                valueType: ValueType.CURRENCY,
                precision: 0,
                tag: 'P&L Data/Realized P & L',
                desc: 'Realized P&L',
            },
            unrealisedPnl: {
                field: 'unrealisedPnl',
                name: toUserCurrencyColumnName('Unrealized P&L'),
                width: 70,
                valueType: ValueType.CURRENCY,
                precision: 0,
                tag: 'P&L Data/Unrealized P&L',
                desc: 'Unrealized P&L',
            },
            totalPnl: {
                field: 'totalPnl',
                name: toUserCurrencyColumnName('Total P&L'),
                width: 70,
                valueType: ValueType.CURRENCY,
                precision: 0,
                tag: 'P&L Data/Total P&L',
                desc: 'Total P&L',
            },
        };
    return { ...ret, ...lastPriceColumns };
};

export function useOutlierColumnDefinitions() {
    const orderColumns = useColumnDefinitions();
    const columnPortfolio = useColumnPortfolio();
    const appContext = useContext(AppContext);
    const {
        config: { avgPriceDecimals, visibleColumns },
    } = useOutliersConfigSlice();
    const isLastPriceEnabled = useSelectIsFeatureEnabled(
        WellKnownFeatureToggleKeys.LastPriceEnabled
    );

    // columns definitions
    const blotterColDefs = useMemo(() => {
        const averagePriceCellRenderer = currencyCellRenderer(
            appContext,
            (context: IndigoAppContextType, value: unknown) =>
                N(context, avgPriceDecimals, value as number)
        );
        const visibleBlotterColumns = visibleColumns.filter(
            (c) => !!columnPortfolio[c as BlotterColumn]
        );
        const cols = blotterColumnDefinitions(
            columnPortfolio,
            visibleBlotterColumns as BlotterColumns,
            appContext,
            { disableSorting: true }
        );
        const avgPxCol = cols.find((c) => c.colId === 'avgPx');
        if (avgPxCol) {
            avgPxCol.cellRenderer = averagePriceCellRenderer;
        }
        const avgPxCCol = cols.find((c) => c.colId === 'avgPxC');
        if (avgPxCCol) {
            avgPxCCol.cellRenderer = averagePriceCellRenderer;
        }
        return cols;
    }, [visibleColumns, columnPortfolio, appContext, avgPriceDecimals]);

    const outlierColDefs = useMemo(
        () =>
            getColDefsForColumnDefinitionsDictionary(
                appContext,
                getAdditionalOutlierColumns(isLastPriceEnabled)
            ),
        [appContext, isLastPriceEnabled]
    );

    return useMemo(
        () => {
            return {
                columnDefinitions: {
                    ...orderColumns,
                    ...getAdditionalOutlierColumns(isLastPriceEnabled),
                },
                colDefs: [...blotterColDefs, ...outlierColDefs],
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            blotterColDefs,
            outlierColDefs,
            orderColumns,
            getAdditionalOutlierColumns,
            isLastPriceEnabled,
        ]
    );
}
